import { useState, useEffect, Suspense, lazy } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ArrowUpRight, Facebook, Instagram, Linkedin } from "lucide-react";
import { CONTACT, SERVICES, LOGO } from "../lib/data";

const AmbientShapes = lazy(() => import("./AmbientShapes"));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, linkedin: Linkedin };

function useWebGL() {
  const [ok, setOk] = useState(false);
  useEffect(() => {
    try {
      const c = document.createElement("canvas");
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      setOk(!reduced && !!(window.WebGLRenderingContext && c.getContext("webgl")));
    } catch {
      setOk(false);
    }
  }, []);
  return ok;
}

export default function Contact() {
  const webgl = useWebGL();
  const [form, setForm] = useState({ name: "", email: "", company: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. We'll be in touch within one business day.");
      setForm({ name: "", email: "", company: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please email us directly at info@futuretechmates.com");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      id="contact"
      data-testid="contact-section"
      className="relative bg-[var(--bg-primary)] overflow-hidden"
    >
      {webgl && (
        <div className="absolute inset-0 pointer-events-none">
          <Suspense fallback={null}>
            <AmbientShapes />
          </Suspense>
        </div>
      )}
      <div className="absolute left-1/2 -top-20 h-[50vw] w-[50vw] max-w-[640px] max-h-[640px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(124,52,235,0.22),transparent_60%)] blur-3xl pointer-events-none" />

      <div className="relative px-6 md:px-12 lg:px-16 pt-24 md:pt-32">
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="font-display font-black uppercase tracking-tighter leading-[0.9] text-white text-[18vw] md:text-[14vw] lg:text-[12vw]"
          data-testid="contact-headline"
        >
          Let&apos;s talk
        </motion.h2>

        <div className="mt-12 md:mt-16 grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Details */}
          <div className="lg:col-span-5 flex flex-col gap-8">
            <p className="text-lg md:text-xl text-zinc-300 max-w-md leading-relaxed">
              Ready to scale? Tell us where you want to go — we&apos;ll bring the strategy,
              creativity and relentless execution to get you there.
            </p>
            <div className="flex flex-col gap-5">
              <a href={CONTACT.phoneHref} data-testid="contact-phone" className="group flex items-center gap-4 text-zinc-300 hover:text-white transition-colors">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 group-hover:border-[var(--accent-primary)] transition-colors"><Phone size={17} /></span>
                {CONTACT.phone}
              </a>
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="group flex items-center gap-4 text-zinc-300 hover:text-white transition-colors">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 group-hover:border-[var(--accent-primary)] transition-colors"><Mail size={17} /></span>
                {CONTACT.email}
              </a>
              <div className="flex items-start gap-4 text-zinc-300">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/10"><MapPin size={17} /></span>
                <span className="max-w-xs">{CONTACT.address}</span>
              </div>
            </div>
            <div className="flex gap-3">
              {CONTACT.socials.map((s) => {
                const Icon = SOCIAL_ICONS[s.icon];
                return (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    data-testid={`social-${s.icon}`}
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 text-zinc-400 hover:text-white hover:border-[var(--accent-secondary)] hover:bg-[var(--accent-secondary)]/10 transition-colors"
                  >
                    <Icon size={18} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Form */}
          <form onSubmit={submit} noValidate data-testid="contact-form" className="lg:col-span-7 glass rounded-3xl p-7 md:p-10">
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Name*" testid="input-name">
                <input value={form.name} onChange={update("name")} data-testid="contact-name" required
                  className="ft-input" placeholder="Jane Doe" />
              </Field>
              <Field label="Email*" testid="input-email">
                <input type="email" value={form.email} onChange={update("email")} data-testid="contact-email-input" required
                  className="ft-input" placeholder="jane@brand.com" />
              </Field>
              <Field label="Company" testid="input-company">
                <input value={form.company} onChange={update("company")} data-testid="contact-company"
                  className="ft-input" placeholder="Your brand" />
              </Field>
              <Field label="Service of interest" testid="input-service">
                <select value={form.service} onChange={update("service")} data-testid="contact-service" className="ft-input">
                  <option value="">Select a service</option>
                  {SERVICES.map((s) => (
                    <option key={s.n} value={s.title} className="bg-[var(--bg-secondary)]">{s.title}</option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="mt-5">
              <Field label="Message*" testid="input-message">
                <textarea value={form.message} onChange={update("message")} data-testid="contact-message" required rows={4}
                  className="ft-input resize-none" placeholder="Tell us about your brand and goals..." />
              </Field>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              data-magnetic
              className="mt-7 inline-flex items-center gap-3 rounded-full bg-[var(--accent-secondary)] px-8 py-4 text-sm font-semibold text-white hover:bg-[var(--accent-secondary-hover)] transition-colors disabled:opacity-60"
            >
              {loading ? "Sending..." : "Send message"}
              <ArrowUpRight size={18} />
            </button>
          </form>
        </div>

        {/* Bottom bar */}
        <div className="mt-20 md:mt-28 border-t border-white/10 py-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center">
            <img src={LOGO} alt="Future Techmates" className="h-9 w-auto" />
          </div>
          <p className="text-sm text-zinc-500">{CONTACT.tagline}</p>
          <p className="text-sm text-zinc-600">© 2026 futuretechmates.com</p>
        </div>
      </div>

      <style>{`
        .ft-input {
          width: 100%;
          background: rgba(255,255,255,0.03);
          border: 1px solid var(--border-subtle);
          border-radius: 0.75rem;
          padding: 0.85rem 1rem;
          color: #fff;
          font-size: 0.95rem;
          transition: border-color .2s ease, background-color .2s ease;
        }
        .ft-input::placeholder { color: #52525b; }
        .ft-input:focus {
          outline: none;
          border-color: var(--accent-primary);
          background: rgba(124,52,235,0.06);
        }
      `}</style>
    </footer>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">
        {label}
      </span>
      {children}
    </label>
  );
}
